import { useEffect, useRef, useState } from 'react';
import { t } from '@/lib/i18n';
import { Button } from './ui';
import { ChevronDown, Download, FileSpreadsheet, FileText, Sheet } from 'lucide-react';

type Format = 'xlsx' | 'csv' | 'pdf';
const LABELS: Record<Format, { label: string; icon: typeof FileText }> = {
  xlsx: { label: 'Excel (.xlsx)', icon: FileSpreadsheet },
  csv: { label: 'CSV', icon: Sheet },
  pdf: { label: 'PDF', icon: FileText },
};

/** "Export" button with a small menu of formats; downloads from /api/exports/<kind>. */
export function ExportMenu({
  kind,
  params,
  formats = ['xlsx', 'csv', 'pdf'],
  extra,
  label,
}: {
  kind: string;
  params: Record<string, string | undefined | null>;
  formats?: Format[];
  extra?: { label: string; href: string }[];
  label?: string;
}) {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (!open) return;
    const close = (e: MouseEvent) => !ref.current?.contains(e.target as Node) && setOpen(false);
    const esc = (e: KeyboardEvent) => e.key === 'Escape' && setOpen(false);
    document.addEventListener('mousedown', close);
    document.addEventListener('keydown', esc);
    return () => {
      document.removeEventListener('mousedown', close);
      document.removeEventListener('keydown', esc);
    };
  }, [open]);
  const href = (f: Format) => {
    const q = new URLSearchParams();
    for (const [k, v] of Object.entries(params)) if (v) q.set(k, v);
    q.set('format', f);
    return `/api/exports/${kind}?${q.toString()}`;
  };
  return (
    <div ref={ref} className="relative inline-block print:hidden">
      <Button variant="secondary" icon={<Download className="h-4 w-4" />} aria-haspopup="menu" aria-expanded={open} onClick={() => setOpen((o) => !o)}>
        {label ?? t('Export')} <ChevronDown className="h-3.5 w-3.5" />
      </Button>
      {open && (
        <div role="menu" className="absolute end-0 z-30 mt-1 w-52 overflow-hidden rounded-lg border border-slate-200 bg-surface py-1 shadow-lg">
          {formats.map((f) => {
            const I = LABELS[f].icon;
            return (
              <a key={f} role="menuitem" href={href(f)} download className="flex items-center gap-2 px-3 py-2 text-sm text-slate-700 hover:bg-slate-50" onClick={() => setOpen(false)}>
                <I className="h-4 w-4 text-slate-400" /> {t(LABELS[f].label)}
              </a>
            );
          })}
          {extra?.map((x) => (
            <a key={x.href} role="menuitem" href={x.href} download className="flex items-center gap-2 border-t border-slate-100 px-3 py-2 text-sm text-slate-700 hover:bg-slate-50" onClick={() => setOpen(false)}>
              <FileText className="h-4 w-4 text-slate-400" /> {x.label}
            </a>
          ))}
        </div>
      )}
    </div>
  );
}
